// ============================================================================
// Theme Management - Extended for Custom Themes
// ============================================================================
// Uses themeSchema.js as single source of truth for CSS variables.
// ============================================================================

import { themeSchema, getDefaultThemeConfig, getSchemaCategories } from '../../config/theme-schema.js';

export const theme = {
    // Schema reference for external access
    schema: themeSchema,
    // Built-in themes (cannot be edited)
    builtInThemes: ['light', 'dark', 'sage'],

    // All available themes (built-in names)
    themes: ['light', 'dark', 'sage'],

    // Current theme identifier (e.g., 'light' or 'custom-uuid')
    current: null,

    // Current custom theme config (if using custom)
    customConfig: null,

    // Current custom theme ID (if using custom)
    customId: null,

    // Theme change listeners
    listeners: new Set(),

    // Initialize theme from localStorage
    init() {
        // Skip if already initialized
        if (this.current) return;

        const saved = localStorage.getItem('aerium-theme') || 'light';
        const customConfig = localStorage.getItem('aerium-custom-theme');
        const customId = localStorage.getItem('aerium-custom-theme-id');

        this.current = saved;

        // Check if using a custom theme
        if (saved.startsWith('custom-') && customConfig) {
            try {
                this.customConfig = JSON.parse(customConfig);
                this.customId = customId;
                this.applyCustomTheme(this.customConfig);
            } catch (e) {
                console.error('[Theme] Failed to parse custom theme:', e);
                // Fallback to light theme
                this.set('light');
            }
        } else {
            // Built-in theme
            if (document.documentElement.getAttribute('data-theme') !== saved) {
                document.documentElement.setAttribute('data-theme', saved);
            }
        }
    },

    // Set a built-in theme
    set(themeName, skipPersist = false) {
        if (!this.builtInThemes.includes(themeName)) {
            return;
        }

        // Skip if already set to this theme (and not custom)
        if (themeName === this.current && !this.customConfig) {
            return;
        }

        // Clear any custom theme styles
        this.clearCustomStyles();
        this.customConfig = null;
        this.customId = null;

        this.current = themeName;
        document.documentElement.setAttribute('data-theme', themeName);

        if (!skipPersist) {
            localStorage.setItem('aerium-theme', themeName);
            localStorage.removeItem('aerium-custom-theme');
            localStorage.removeItem('aerium-custom-theme-id');
        }

        // Notify listeners
        this.listeners.forEach(fn => fn(themeName, null));
    },

    // Apply a custom theme configuration
    setCustom(themeId, themeConfig, skipPersist = false) {
        const identifier = `custom-${themeId}`;

        this.current = identifier;
        this.customConfig = themeConfig;
        this.customId = themeId;

        // Remove built-in theme attribute
        document.documentElement.removeAttribute('data-theme');

        // Apply custom CSS variables
        this.applyCustomTheme(themeConfig);

        if (!skipPersist) {
            localStorage.setItem('aerium-theme', identifier);
            localStorage.setItem('aerium-custom-theme', JSON.stringify(themeConfig));
            localStorage.setItem('aerium-custom-theme-id', themeId);
        }

        // Notify listeners
        this.listeners.forEach(fn => fn(identifier, themeConfig));
    },

    // Apply custom theme CSS variables to :root
    applyCustomTheme(config) {
        const root = document.documentElement;

        // Set color-scheme if specified
        if (config['color-scheme']) {
            root.style.colorScheme = config['color-scheme'];
        }

        // Apply all CSS variables
        Object.entries(config).forEach(([variable, value]) => {
            if (variable !== 'color-scheme' && variable.startsWith('--')) {
                root.style.setProperty(variable, value);
            }
        });
    },

    // Clear custom theme inline styles from :root
    clearCustomStyles() {
        const root = document.documentElement;
        root.style.cssText = '';
        root.style.colorScheme = '';
    },

    // Preview a theme temporarily (for live editing in theme creator)
    preview(themeConfig) {
        this.applyCustomTheme(themeConfig);
    },

    // Cancel preview and restore current theme
    cancelPreview() {
        this.clearCustomStyles();

        if (this.customConfig) {
            // Restore custom theme
            this.applyCustomTheme(this.customConfig);
        } else if (this.current && this.builtInThemes.includes(this.current)) {
            // Restore built-in theme
            document.documentElement.setAttribute('data-theme', this.current);
        }
    },

    // Toggle between built-in themes
    toggle() {
        const currentIndex = this.builtInThemes.indexOf(this.current);
        const nextIndex = (currentIndex + 1) % this.builtInThemes.length;
        this.set(this.builtInThemes[nextIndex]);
    },

    // Get current theme identifier
    get() {
        return this.current;
    },

    // Check if currently using a custom theme
    isCustom() {
        return this.current?.startsWith('custom-') || false;
    },

    // Get the ID of the current custom theme (if using one)
    getCustomId() {
        return this.customId;
    },

    // Get the current custom theme config (if using one)
    getCustomConfig() {
        return this.customConfig;
    },

    // Check if a specific theme ID is currently active
    isActive(themeIdOrName) {
        if (this.builtInThemes.includes(themeIdOrName)) {
            return this.current === themeIdOrName && !this.customConfig;
        }
        return this.customId === themeIdOrName;
    },

    // Subscribe to theme changes
    onChange(callback) {
        this.listeners.add(callback);
        // Return unsubscribe function
        return () => this.listeners.delete(callback);
    },

    // Get default theme config from schema (single source of truth)
    getDefaultConfig() {
        return getDefaultThemeConfig();
    },

    // Get schema categories for UI rendering
    getSchemaCategories() {
        return getSchemaCategories();
    }
};
